// Forces the leading locale segment of a saved storefront URL to match the
// caller-provided active locale. Fix for WI #8527 (desktop "Voltar" lost the
// /pt-br/ prefix). The active locale MUST come from
// window.location.pathname via getUrlLocale("lower") — i18next.language and
// sessionStorage are unreliable here (see WI #8320).
// Keep this in sync with the ACTIVE_LOCALES list in getUrlLocale.ts.
// Verified empirically against the live picker (AB#8527): Colombia market
// serves `/pt/`, `/es/`, `/it/`, `/fr/`, `/de/`, `/nl/`, `/da/` (and `en`
// without a prefix). Regional forms (pt-BR, es-ES, en-GB) are kept for
// non-URL callers that may still pass the regional code.
const ACTIVE_LOCALES = new Set([
    "en",
    "pt",
    "pt-BR",
    "de",
    "it",
    "fr",
    "es",
    "es-ES",
    "en-GB",
    "nl",
    "da",
]);
// Locale-shape segment: two letters, optionally `-XX`. Case-insensitive
// because Shopify sometimes lowercases the region segment in redirects.
const LOCALE_SEGMENT_RE = /^[a-z]{2}(-[a-z]{2})?$/i;
function normalizeLocaleSegment(segment) {
    const dash = segment.indexOf("-");
    if (dash === -1) {
        return segment.toLowerCase();
    }
    return `${segment.slice(0, dash).toLowerCase()}-${segment.slice(dash + 1).toUpperCase()}`;
}
function isActiveLocaleSegment(segment) {
    if (!LOCALE_SEGMENT_RE.test(segment)) {
        return false;
    }
    return ACTIVE_LOCALES.has(normalizeLocaleSegment(segment));
}
/**
 * Returns `searchUrl` with the locale segment that sits immediately before
 * `/collections` forced to match `locale`:
 *
 *   - If `locale` is "en", any leading locale segment is stripped (English
 *     default has no URL prefix).
 *   - Otherwise the segment is replaced (or inserted) with the lowercase
 *     canonical form (`pt-br`, `es-es`, `en-gb`, …) Shopify Markets uses in
 *     its URLs.
 *
 * URLs that do not contain `/collections` (e.g. the `/` fallback) are
 * returned unchanged.
 *
 * @param searchUrl  the URL saved in `sessionStorage["searchUrl"]`; may be
 *                   absolute (`https://host/...`) or path-relative (`/...`).
 * @param locale     one of the 8 active locales returned by
 *                   `getUrlLocale("lower")`.
 */
export function applyLocalePrefixToSearchUrl(searchUrl, locale) {
    if (!searchUrl || !searchUrl.includes("/collections")) {
        return searchUrl;
    }
    const segments = searchUrl.split("/");
    const collectionsIndex = segments.indexOf("collections");
    if (collectionsIndex < 1) {
        return searchUrl;
    }
    const candidate = segments[collectionsIndex - 1];
    const desiredPrefix = locale === "en" ? "" : locale.toLowerCase();
    const hasLocaleSegment = isActiveLocaleSegment(candidate);
    if (hasLocaleSegment) {
        if (desiredPrefix === "") {
            segments.splice(collectionsIndex - 1, 1);
        }
        else {
            segments[collectionsIndex - 1] = desiredPrefix;
        }
    }
    else if (desiredPrefix !== "") {
        segments.splice(collectionsIndex, 0, desiredPrefix);
    }
    return segments.join("/");
}
export default applyLocalePrefixToSearchUrl;
